import React from 'react';
import { DocumentTextIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const Terms: React.FC = () => {
  const sections = [
    {
      title: 'Acceptance of Terms',
      content: [
        'By accessing and using Maasai SightTrails Safaris website and services, you accept and agree to be bound by the terms and provision of this agreement.',
        'If you do not agree to abide by the above, please do not use this service.',
        'These terms apply to all visitors, users, and others who access or use the service.'
      ]
    },
    {
      title: 'Booking and Payment Terms',
      content: [
        'All bookings are subject to availability and confirmation by Maasai SightTrails Safaris.',
        'A deposit of 30% of the total tour cost is required to confirm your booking.',
        'Full payment must be received 30 days before the departure date.',
        'Prices are subject to change without notice until booking is confirmed.',
        'All prices include taxes, service charges, and fees as specified in the tour description.'
      ]
    },
    {
      title: 'Cancellation and Refund Policy',
      content: [
        'Cancellations made 60+ days before departure: Full refund minus processing fees.',
        'Cancellations made 30-59 days before departure: 75% refund.',
        'Cancellations made 15-29 days before departure: 50% refund.',
        'Cancellations made within 14 days of departure: No refund.',
        'Refunds will be processed within 14 business days of cancellation request.',
        'No refunds for unused services or early departures.'
      ]
    },
    {
      title: 'Travel Requirements and Responsibilities',
      content: [
        'Travelers are responsible for obtaining all required travel documents including passports, visas, and vaccinations.',
        'Travelers must inform us of any medical conditions, dietary restrictions, or special needs at the time of booking.',
        'Travelers are responsible for their own travel insurance and medical coverage.',
        'Travelers must comply with all local laws, regulations, and customs.',
        'Travelers are responsible for their personal belongings and valuables.'
      ]
    },
    {
      title: 'Limitation of Liability',
      content: [
        'Maasai SightTrails Safaris acts as an agent for various service providers and is not liable for their actions or omissions.',
        'We are not responsible for delays, cancellations, or changes due to weather, political unrest, or other circumstances beyond our control.',
        'Our liability is limited to the amount paid for the tour services.',
        'We are not liable for personal injury, loss, or damage to personal property.',
        'Travelers participate in activities at their own risk.'
      ]
    },
    {
      title: 'Force Majeure',
      content: [
        'We are not liable for any failure to perform our obligations due to circumstances beyond our reasonable control.',
        'Such circumstances include but are not limited to: natural disasters, war, terrorism, government actions, or pandemics.',
        'In such cases, we will make reasonable efforts to provide alternative arrangements or refunds.',
        'No compensation will be paid for inconvenience or disappointment caused by such circumstances.'
      ]
    }
  ];

  const importantNotes = [
    {
      icon: ExclamationTriangleIcon,
      title: 'Health and Safety',
      note: 'Safari activities involve inherent risks. Participants must be in good health and follow all safety instructions.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Insurance',
      note: 'We strongly recommend comprehensive travel insurance covering medical emergencies, trip cancellation, and evacuation.'
    },
    {
      icon: DocumentTextIcon,
      title: 'Documentation',
      note: 'All travelers must have valid passports and required visas. We can assist with visa applications but are not responsible for visa denials.'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <div className="bg-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Please read these terms and conditions carefully before booking your safari adventure.
          </p>
          <div className="mt-6 text-sm opacity-75">
            Last updated: January 15, 2024
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Terms Image Banner */}
        <div className="relative rounded-lg overflow-hidden shadow-sm mb-12">
          <img
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Legal terms and conditions"
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-primary-900/70 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-2xl font-bold mb-2">Terms & Conditions</h2>
              <p className="text-lg">Please read these terms carefully before booking</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <DocumentTextIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-primary-900 mb-4">Agreement to Terms</h2>
              <p className="text-neutral-600 leading-relaxed">
                These Terms of Service ("Terms") govern your use of Maasai SightTrails Safaris website and services.
                By accessing our website or booking our services, you agree to be bound by these Terms.
                If you disagree with any part of these terms, you may not access our services.
              </p>
            </div>
          </div>
        </div>

        {/* Main Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-neutral-200">
              <div className="p-6 border-b border-neutral-200">
                <h2 className="text-2xl font-bold text-primary-900">{section.title}</h2>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start text-neutral-700">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Important Notes */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mt-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-6">Important Notes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {importantNotes.map((note, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <note.icon className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-primary-900 mb-2">{note.title}</h3>
                <p className="text-neutral-600 text-sm">{note.note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Modifications */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mt-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">Modifications to Terms</h2>
          <p className="text-neutral-600 mb-4">
            We reserve the right to modify these Terms at any time. We will notify users of any material changes
            by posting the new Terms on this page and updating the "Last updated" date. Your continued use of our
            services after any modifications constitutes acceptance of the new Terms.
          </p>
          <p className="text-neutral-600">
            If you do not agree to the modified Terms, you must discontinue your use of our services.
          </p>
        </div>

        {/* Governing Law */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mt-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">Governing Law</h2>
          <p className="text-neutral-600 mb-4">
            These Terms shall be governed by and construed in accordance with the laws of Kenya, without regard
            to its conflict of law provisions. Any disputes arising from these Terms or your use of our services
            shall be subject to the exclusive jurisdiction of the courts of Kenya.
          </p>
        </div>

        {/* Contact Information */}
        <div className="bg-primary-50 rounded-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">Questions About These Terms?</h2>
          <p className="text-neutral-600 mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-primary-900 mb-2">Legal Department</h3>
              <p className="text-neutral-700">sighttrailsafaris@gmail.com</p>
              <p className="text-neutral-700">+254 708 560 665</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary-900 mb-2">Mailing Address</h3>
              <p className="text-neutral-700">
                Maasai SightTrails Safaris<br />
                Legal Department<br />
                P.O. Box 12345<br />
                Nairobi, Kenya
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
